import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { LuUsers, LuUserPlus, LuUserCheck, LuTrendingUp, LuBanknote, LuDownload, LuChevronRight } from "react-icons/lu";
import SearchInput from "../../components/search-input";
import Table from "../../components/table";
import useRequest from "../../components/hooks/use-request";
import Icon from "../../assets/icons";
import DateRange from "../../components/date-range";
import { formatCurrency, downloadCsv } from "../../utilities/functions";
import { showToast } from "../../components/toast";
import { TailSpin } from "react-loader-spinner";

const parseDateRange = (range: string): { startDate?: string; endDate?: string } => {
  if (!range) return {};
  const parts = range.split(" - ");
  if (parts.length !== 2) return {};
  const toISO = (dmy: string) => {
    const [d, m, y] = dmy.trim().split("/");
    return `${y}-${m}-${d}`;
  };
  return { startDate: toISO(parts[0]), endDate: toISO(parts[1]) };
};

const formatXAxisDate = (dateStr: string) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-3 text-[12px]">
      <p className="text-gray-500 mb-1 font-medium">{label}</p>
      <p className="font-semibold text-[#0979A1]">Transactions: {(payload[0]?.value || 0).toLocaleString()}</p>
      <p className="text-gray-600 mt-0.5">Volume: {formatCurrency(payload[0]?.payload?.volume || 0, "NGN", "symbol")}</p>
    </div>
  );
};

const avatarColors = ["#0979A1", "#E85D04", "#7B2D8B", "#2D6A4F", "#D62828", "#023E8A", "#B5179E"];
const avatarColor = (name: string) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

const UserAvatar = ({ name }: { name: string }) => (
  <div
    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[12px] font-semibold shrink-0"
    style={{ backgroundColor: avatarColor(name) }}>
    {name?.charAt(0).toUpperCase()}
  </div>
);

const RankBadge = ({ rank }: { rank: number }) => {
  if (rank === 1) return <Icon name="goldMedal" />;
  if (rank === 2) return <Icon name="silverMedal" />;
  if (rank === 3) return <Icon name="bronzeMedal" />;
  return <span className="text-[14px] font-semibold text-gray-600 pl-1">{rank}</span>;
};

const StatCard = ({ icon, iconBg, title, value, link }: any) => (
  <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-[12px] text-gray-500 break-words">{title}</p>
      <div className="flex items-center gap-2">
        <p className="text-[18px] font-bold leading-tight break-words min-w-0">{value}</p>
        {link && (
          <Link to={link} className="text-[#0979A1] text-[12px] font-medium flex items-center">
            See all <LuChevronRight size={13} />
          </Link>
        )}
      </div>
    </div>
  </div>
);

const ReferralProgram = () => {
  const userToken = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${userToken}` };

  const [stats, setStats] = useState<any>({});
  const [chartData, setChartData] = useState<any[]>([]);
  const [topAssociates, setTopAssociates] = useState<any[]>([]);
  const [associates, setAssociates] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dateRange, setDateRange] = useState("");
  const [exporting, setExporting] = useState(false);

  // Default range spans Jan 1 2024 → today until the user picks a range
  const dateDefaults = useMemo(() => {
    const now = new Date();
    const start = new Date(2024, 0, 1);
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const label = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return {
      startDate: iso(start),
      endDate: iso(now),
      label: `${label(start)} - ${label(now)}`,
    };
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : "";
      await downloadCsv(
        `/marketers/export-with-leaderboard${query}`,
        "sales-associates-export.csv"
      );
    } catch (err: any) {
      showToast(err?.message || "No data to export", false, {
        position: "top-center",
      });
    } finally {
      setExporting(false);
    }
  };

  const { makeRequest: getSummary } = useRequest("/marketers/summary", "GET", headers);
  const { makeRequest: getLeaderboard } = useRequest("/marketers/leaderboard", "GET", headers);
  const { makeRequest: getGraph, loading: chartLoading } = useRequest("/marketers/referral-transactions/graph", "GET", headers);
  const { makeRequest: getAssociates, loading: associatesLoading } = useRequest("/marketers", "GET", headers);

  useEffect(() => {
    const fetch = async () => {
      const [res] = await getSummary();
      if (res?.data) setStats(res.data);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetch = async () => {
      const [res] = await getLeaderboard();
      if (res?.data) setTopAssociates(res.data.slice(0, 5));
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetch = async () => {
      const picked = parseDateRange(dateRange);
      const params = Object.keys(picked).length
        ? picked
        : { startDate: dateDefaults.startDate, endDate: dateDefaults.endDate };
      const [res] = await getGraph(undefined, params);
      if (res?.data) setChartData(res.data);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    const fetch = async () => {
      const params: Record<string, any> = { page: currentPage };
      if (search) params.search = search;
      if (userTypeFilter) params.userType = userTypeFilter;
      const [res] = await getAssociates(undefined, params);
      if (res?.data) {
        setAssociates(res.data.marketers || []);
        setTotalPages(res.data.totalPages || 1);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, search, userTypeFilter]);

  const associateColumns = [
    {
      header: "Name", accessor: "fullname",
      render: (row: any) => (
        <div className="flex items-center gap-2">
          <UserAvatar name={row.fullname || ""} />
          <span className="text-[13px] font-medium">{row.fullname}</span>
        </div>
      ),
    },
    { header: "Email", accessor: "email" },
    {
      header: "Referral Code", accessor: "referralCode",
      render: (row: any) => (
        <span className="bg-[#E8F7FA] text-[#0979A1] px-2 py-0.5 rounded text-[12px] font-semibold uppercase">
          {row.referralCode}
        </span>
      ),
    },
    { header: <span className="block text-center py-1 text-xs">Referred<br/>Buyers</span>, accessor: "referredBuyers" },
    { header: <span className="block text-center py-1 text-xs">Referred<br/>Merchants</span>, accessor: "referredMerchants" },
    { header: <span className="block text-center py-1 text-xs">Activated<br/>Buyers</span>, accessor: "activatedBuyers" },
    { header: <span className="block text-center py-1 text-xs">Activated<br/>Merchants</span>, accessor: "activatedMerchants" },
    { header: <span className="block text-center py-1 text-xs">Buyer<br/>Transactions</span>, accessor: "buyersTransaction" },
    { header: <span className="block text-center py-1 text-xs">Merchant<br/>Transactions</span>, accessor: "merchantsTransaction" },
  ];

  return (
    <>
      <div className="flex justify-between items-center w-[95%]">
        <h2 className="text-[24px] font-bold">Overview</h2>
        <div className="flex items-center gap-3">
          <DateRange placeholder={dateRange || dateDefaults.label} onChange={setDateRange} />
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 text-[14px] border border-gray-300 rounded-lg px-3 py-2 font-medium hover:bg-gray-50 disabled:opacity-60">
            {exporting ? (
              <TailSpin color="#0979A1" height={15} width={15} />
            ) : (
              <LuDownload size={15} />
            )}
            Export
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-5 w-[95%]">
        <StatCard icon={<LuUsers size={18} color="#0979A1" />} iconBg="bg-[#E8F7FA]" title="Total Sales Associates" value={(stats.totalSalesAssociate ?? 0).toLocaleString()} />
        <StatCard icon={<LuUserPlus size={18} color="#E85D04" />} iconBg="bg-orange-50" title="Total Referred Users" value={(stats.totalReferredUsers ?? 0).toLocaleString()} link="/referral-program/referred-users" />
        <StatCard icon={<LuUserCheck size={18} color="#7B2D8B" />} iconBg="bg-purple-50" title="Total Activated Users" value={(stats.totalActivatedUsers ?? 0).toLocaleString()} />
        <StatCard icon={<LuTrendingUp size={18} color="#2D6A4F" />} iconBg="bg-green-50" title="Completed Transactions" value={(stats.completedOrders ?? 0).toLocaleString()} />
        <StatCard icon={<LuBanknote size={18} color="#D62828" />} iconBg="bg-red-50" title="Transaction Value" value={formatCurrency(stats.totalTransactionValue ?? 0, "NGN", "symbol") ?? "₦0.00"} />
      </div>

      {/* Chart + Top Associates */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mt-6 w-[95%]">
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-[16px] font-semibold mb-4">Referral Transactions Over Time</h3>
          {chartLoading ? (
            <div className="flex justify-center items-center h-[250px]">
              <TailSpin color="skyblue" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="txnGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0979A1" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#0979A1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={formatXAxisDate} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="count" stroke="#0979A1" strokeWidth={2} fill="url(#txnGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-[16px] font-semibold">Top Sales Associates</h3>
            <Link to="/referral-program/top-associates" className="text-[#0979A1] text-[13px] font-medium">See all</Link>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[280px]">
              <div className="grid grid-cols-12 text-[11px] text-gray-500 font-medium pb-2 border-b gap-2">
                <span className="col-span-2">Rank</span>
                <span className="col-span-4">Associate</span>
                <span className="col-span-2 text-right">Txns</span>
                <span className="col-span-4 text-right">Value</span>
              </div>
              {topAssociates.map((a: any, i: number) => (
                <div key={i} className="grid grid-cols-12 text-[12px] py-2.5 border-b border-gray-50 items-center gap-2">
                  <div className="col-span-2"><RankBadge rank={i + 1} /></div>
                  <div className="col-span-4 flex items-center gap-1.5">
                    <UserAvatar name={a.name || ""} />
                    <span className="font-medium truncate text-[11px]">{a.name}</span>
                  </div>
                  <span className="col-span-2 text-right">{a.numberOfTransactions}</span>
                  <span className="col-span-4 text-right text-[11px]">{formatCurrency(a.totalTransactionValue || 0, "NGN", "symbol")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sales Associates table */}
      <div className="mt-6 w-[95%]">
        <h3 className="text-[18px] font-semibold mb-3">Sales Associates</h3>
        <div className="flex items-center justify-between mb-3">
          <SearchInput
            placeholder="Search by name, email, referral code etc"
            onSearch={(val) => { setSearch(val); setCurrentPage(1); }}
            className="w-80"
          />
          <select
            value={userTypeFilter}
            onChange={(e) => { setUserTypeFilter(e.target.value); setCurrentPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none">
            <option value="">All User Types</option>
            <option value="buyer">Buyer</option>
            <option value="merchant">Merchant</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {associatesLoading ? (
          <div className="flex justify-center items-center h-64">
            <TailSpin color="skyblue" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table columns={associateColumns} data={associates} onUserClick={() => {}} selectedUserId={null} sn={false} />
          </div>
        )}
        <div className="mt-4 flex justify-between items-center text-[13px] text-gray-600">
          <span>{associates.length} results &bull; page {currentPage} of {totalPages}</span>
          <div className="flex items-center gap-3">
            <span>{currentPage} of {totalPages} pages</span>
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferralProgram;
